import { useEffect, useState } from 'react';
import { LogOut, User as UserIcon, Bell, Users, Sun, Moon, Lock, Shield, Upload } from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useToast } from '@/contexts/ToastContext';
import { profileService } from '@/services/api';
import { UserAvatar } from '@/components/ui/Avatar';
import { cn } from '@/lib/utils';

const SECTIONS = [
  ['profile', 'Edit profile'],
  ['password', 'Change password'],
  ['appearance', 'Appearance'],
  ['notifications', 'Notifications'],
  ['privacy', 'Privacy'],
  ['account', 'Account'],
] as const;

type SectionKey = (typeof SECTIONS)[number][0];

function readPref(key: string, fallback: boolean) {
  const saved = localStorage.getItem(key);
  return saved === null ? fallback : saved === 'true';
}

export default function SettingsPage() {
  const { user, profile, signOut, updatePassword } = useAuth();
  const { toggleTheme, theme } = useTheme();
  const { toast } = useToast();
  const [activeSection, setActiveSection] = useState<SectionKey>('profile');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [editName, setEditName] = useState(profile?.display_name || '');
  const [editBio, setEditBio] = useState(profile?.bio || '');
  const [editLocation, setEditLocation] = useState(profile?.location || '');
  const [editWebsite, setEditWebsite] = useState(profile?.website || '');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [pushEnabled, setPushEnabled] = useState(() => readPref('kelike-pref-push', true));
  const [emailEnabled, setEmailEnabled] = useState(() => readPref('kelike-pref-email', true));

  // Profile can arrive after the first render (auth is async).
  useEffect(() => {
    if (!profile) return;
    setEditName(profile.display_name || '');
    setEditBio(profile.bio || '');
    setEditLocation(profile.location || '');
    setEditWebsite(profile.website || '');
  }, [profile]);

  const togglePref = (key: string, value: boolean, setter: (v: boolean) => void) => {
    setter(value);
    localStorage.setItem(key, String(value));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast('Invalid file', 'Please select an image.', 'destructive');
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleUploadAvatar = async () => {
    if (!user || !avatarFile) return;
    setLoading(true);
    const { data, error } = await profileService.uploadAvatar(user.id, avatarFile);
    if (error) {
      toast('Failed', error.message, 'destructive');
    } else {
      toast('Profile picture updated');
      setAvatarFile(null);
      setAvatarPreview(null);
      // The public avatar URL updates immediately; the new image will show
      // on the next render/page load.
    }
    setLoading(false);
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setLoading(true);
    const { error } = await profileService.updateProfile(user.id, {
      display_name: editName || null,
      bio: editBio || null,
      location: editLocation || null,
      website: editWebsite || null,
    });
    if (error) {
      toast('Failed', error.message, 'destructive');
    } else {
      toast('Profile updated');
    }
    setLoading(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast('Passwords do not match', '', 'destructive');
      return;
    }
    const { error } = await updatePassword(newPassword);
    if (error) {
      toast('Failed', error.message, 'destructive');
    } else {
      toast('Password changed');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  const sectionIcons: Record<SectionKey, any> = {
    profile: UserIcon,
    password: Lock,
    appearance: theme === 'dark' ? Sun : Moon,
    notifications: Bell,
    privacy: Shield,
    account: LogOut,
  };

  const Switch = ({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) => (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        on ? 'bg-brand-600' : 'bg-neutral-300 dark:bg-neutral-700',
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
          on ? 'translate-x-[1.375rem]' : 'translate-x-0.5',
        )}
      />
    </button>
  );

  return (
    <div className="mx-auto max-w-4xl md:flex md:gap-8 px-4 pt-4">
      <div className="md:w-56 md:shrink-0">
        <div className="mb-4 hidden items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900 md:flex">
          <UserAvatar src={profile?.avatar_url} size="md" />
          <div className="min-w-0">
            <p className="truncate font-semibold">{profile?.display_name || user?.email}</p>
            <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">@{profile?.username}</p>
          </div>
        </div>

        <nav className="flex gap-1 overflow-x-auto pb-2 md:flex-col md:space-y-1 md:overflow-visible md:pb-0">
          {SECTIONS.map(([key, label]) => {
            const Icon = sectionIcons[key];
            return (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={cn(
                  'flex shrink-0 items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors md:w-full md:gap-3',
                  activeSection === key
                    ? 'bg-brand-50 text-brand-600 dark:bg-brand-900/20 dark:text-brand-400'
                    : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800',
                )}
              >
                <Icon size={18} />
                <span className="whitespace-nowrap">{label}</span>
              </button>
            );
          })}
        </nav>

        <div className="mt-4 hidden md:block">
          <Button onClick={signOut} variant="destructive" className="w-full">
            Log out
          </Button>
        </div>
      </div>

      <div className="min-w-0 flex-1">
        {activeSection === 'profile' && (
          <div className="max-w-2xl">
            <h2 className="mb-6 text-2xl font-bold">Edit profile</h2>
            <div className="mb-6 flex items-center gap-4">
              <div className="relative shrink-0">
                <UserAvatar
                  src={avatarPreview || profile?.avatar_url}
                  size="lg"
                  isOnline={false}
                />
                <label className="absolute bottom-0 right-0 flex items-center justify-center w-8 h-8 rounded-full bg-brand-600 text-white shadow-md cursor-pointer hover:bg-brand-700 transition-colors" aria-label="Change profile picture">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="sr-only"
                  />
                  <Upload size={16} />
                </label>
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  JPG or PNG, max 5 MB
                </p>
                {avatarFile && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleUploadAvatar}
                    disabled={loading}
                    className="mt-1"
                  >
                    {loading ? 'Uploading…' : 'Upload'}
                  </Button>
                )}
              </div>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleSaveProfile(); }} className="space-y-4">
              <Input label="Display name" value={editName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditName(e.target.value)} />
              <div>
                <label className="mb-1.5 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Bio</label>
                <textarea
                  value={editBio}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditBio(e.target.value)}
                  className="input-field min-h-[80px] w-full border border-neutral-300 bg-white px-4 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
                  rows={3}
                  maxLength={160}
                />
              </div>
              <Input label="Location" value={editLocation} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditLocation(e.target.value)} />
              <Input label="Website" value={editWebsite} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditWebsite(e.target.value)} />
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Saving...' : 'Save changes'}
              </Button>
            </form>
          </div>
        )}

        {activeSection === 'password' && (
          <div className="max-w-md">
            <h2 className="mb-6 text-2xl font-bold">Change password</h2>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <Input label="New password" type="password" value={newPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)} minLength={6} />
              <Input label="Confirm new password" type="password" value={confirmPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)} />
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Changing...' : 'Change password'}
              </Button>
            </form>
          </div>
        )}

        {activeSection === 'appearance' && (
          <div className="max-w-md">
            <h2 className="mb-6 text-2xl font-bold">Appearance</h2>
            <div className="flex items-center justify-between gap-4 rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
              <div>
                <h3 className="font-semibold">Dark mode</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Use dark appearance</p>
              </div>
              <Switch on={theme === 'dark'} onClick={toggleTheme} label="Toggle dark mode" />
            </div>
          </div>
        )}

        {activeSection === 'notifications' && (
          <div className="max-w-md">
            <h2 className="mb-6 text-2xl font-bold">Notifications</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4 rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
                <div>
                  <h3 className="font-semibold">Push notifications</h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Show in-app notifications</p>
                </div>
                <Switch on={pushEnabled} onClick={() => togglePref('kelike-pref-push', !pushEnabled, setPushEnabled)} label="Toggle push notifications" />
              </div>
              <div className="flex items-center justify-between gap-4 rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
                <div>
                  <h3 className="font-semibold">Email notifications</h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Receive email updates</p>
                </div>
                <Switch on={emailEnabled} onClick={() => togglePref('kelike-pref-email', !emailEnabled, setEmailEnabled)} label="Toggle email notifications" />
              </div>
            </div>
          </div>
        )}

        {activeSection === 'privacy' && (
          <div className="max-w-md">
            <h2 className="mb-6 text-2xl font-bold">Privacy</h2>
            <div className="rounded-xl border border-neutral-200 bg-white p-4 text-sm text-neutral-600 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400">
              Private accounts are not available yet. Your posts are visible to everyone.
            </div>
          </div>
        )}

        {activeSection === 'account' && (
          <div className="max-w-md">
            <h2 className="mb-6 text-2xl font-bold">Account</h2>
            <div className="rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Signed in as</p>
              <p className="mt-1 break-all font-medium">{user?.email}</p>
            </div>
          </div>
        )}

        <div className="mt-8 md:hidden">
          <Button onClick={signOut} variant="destructive" className="w-full">
            Log out
          </Button>
        </div>
      </div>
    </div>
  );
}
